import type { ScreenerFilterTab } from "../../../api/services/stock-screener.type";
import {
  ALL_FILTER_FIELDS,
  EMPTY_FILTERS,
  type ScreenerFilterFieldKey,
  type ScreenerFilterValues,
} from "./screenerFilterFields";

const FILTER_TAB_VALUES: ScreenerFilterTab[] = [
  "descriptive",
  "fundamental",
  "technical",
  "all",
];

const isFilterTab = (value: string): value is ScreenerFilterTab =>
  FILTER_TAB_VALUES.includes(value as ScreenerFilterTab);

const isFilterFieldKey = (key: string): key is ScreenerFilterFieldKey =>
  (ALL_FILTER_FIELDS as string[]).includes(key);

export interface ScreenerUrlState {
  filters: ScreenerFilterValues;
  filterTab: ScreenerFilterTab;
  page: number;
}

export const parseScreenerSearchParams = (
  params: URLSearchParams,
): ScreenerUrlState | null => {
  const hasAny =
    params.has("halal") ||
    params.has("tab") ||
    params.has("page") ||
    ALL_FILTER_FIELDS.some((key) => params.has(key));

  if (!hasAny) return null;

  const filters: ScreenerFilterValues = { ...EMPTY_FILTERS };

  for (const key of ALL_FILTER_FIELDS) {
    const value = params.get(key);
    if (value) filters[key] = value;
  }

  if (params.get("halal") === "1" || params.get("halal") === "true") {
    filters.halalOnly = true;
  }

  const tabParam = params.get("tab") ?? "";
  const filterTab = isFilterTab(tabParam) ? tabParam : "descriptive";

  const pageRaw = Number(params.get("page"));
  const page =
    Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1;

  return { filters, filterTab, page };
};

export const buildScreenerShareUrl = (
  filters: ScreenerFilterValues,
  filterTab: ScreenerFilterTab,
  page: number,
): string => {
  const url = new URL(window.location.href);
  url.search = "";

  url.searchParams.set("tab", filterTab);
  if (page > 1) url.searchParams.set("page", String(page));
  if (filters.halalOnly) url.searchParams.set("halal", "1");

  for (const key of ALL_FILTER_FIELDS) {
    const value = filters[key];
    if (value) url.searchParams.set(key, value);
  }

  return url.toString();
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // fall through to legacy copy
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!ok) throw new Error("copy failed");
};

/** Guard against unknown keys when restoring shared URLs. */
export const sanitizeFiltersFromUrl = (
  filters: ScreenerFilterValues,
): ScreenerFilterValues => {
  const next: ScreenerFilterValues = { ...EMPTY_FILTERS, halalOnly: filters.halalOnly };
  for (const key of ALL_FILTER_FIELDS) {
    if (isFilterFieldKey(key)) next[key] = filters[key] ?? "";
  }
  return next;
};
